import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ConfigurationDto } from '@/api-sdk';
import {
  ConfiguratorContextType,
  GlobalComponentType,
  configuratorGlobal,
  global,
} from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { buttonStyles } from '@/styles';
import { Mode } from '@/types';

import '../../usps/fg-usps';
import '../summary/fg-configurator-actions-top-bottom-buttons';

@customElement('fg-configurator-mobile-actions')
@localized()
export class FGConfiguratorMobileActions extends LitElement {
  @property({ type: String })
  queryString?: string;

  /**
   * Configuration {@link ConfigurationDto} data from the api call, is used to validate the CTA buttons if not set
   *
   * @property {ConfigurationDto} configuration
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * Boolean to check if the quantity is validated
   *
   * @property {boolean} quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  /**
   * Boolean to check if the product is unprinted
   */
  @property({ type: Boolean })
  unprinted = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  resetConfigurator() {
    this.dispatchEvent(
      new CustomEvent('reset-configurator', { composed: true }),
    );
  }

  toggleConfigurator() {
    this.dispatchEvent(
      new CustomEvent('toggle-configurator', { composed: true }),
    );
  }

  render() {
    const { editing, salable, usps } = this.configuratorContext;
    const { layout, loggedIn } = this.globalContext;

    return html`
      ${layout === 'wholesale'
        ? html`
            <fg-login-cta part=${layout}></fg-login-cta>
          `
        : nothing}
      ${salable && (loggedIn || layout === 'retail')
        ? html`
            <div class="button-group">
              ${this.queryString || editing
                ? html`
                    <button
                      class="button button-cta"
                      @click=${this.toggleConfigurator}
                    >
                      ${t('actions.continueConfiguration')}
                    </button>
                    <button
                      class="button button-tertiary"
                      @click=${this.resetConfigurator}
                    >
                      ${t('actions.resetConfiguration')}
                    </button>
                  `
                : html`
                    <button
                      class="button button-cta"
                      @click=${() => {
                        this.toggleConfigurator();
                        this.dispatchEvent(
                          new CustomEvent('set-mode-status', {
                            detail: {
                              mode: Mode.DEFAULT,
                            },
                            composed: true,
                          }),
                        );
                      }}
                    >
                      ${t('actions.startConfiguration')}
                    </button>
                  `}
              ${this.queryString || editing || layout === 'wholesale'
                ? nothing
                : html`
                    <div class="or-container">
                      <span class="delimiter"></span>
                      <span class="or-text">${t('labels.or')}</span>
                      <span class="delimiter"></span>
                    </div>
                    <fg-configurator-actions-top-bottom-buttons
                      .configuration=${this.configuration}
                      .quantityValidated=${this.quantityValidated}
                      .unprinted=${this.unprinted}
                      class="top-bottom-buttons"
                      isMobileActionsEnabled
                    ></fg-configurator-actions-top-bottom-buttons>
                  `}
            </div>
          `
        : nothing}
      ${layout === 'retail'
        ? html`
            <fg-usps
              .usps=${usps}
              .type=${salable ? 'salable mobile' : 'mobile'}
            ></fg-usps>
          `
        : nothing}
    `;
  }

  static styles = [
    buttonStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      .delimiter {
        display: block;
        width: 100%;
        height: 1px;
        background-color: var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .or-text {
        margin-left: 15px;
        margin-right: 15px;
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-size: 0.9rem;
      }

      .or-container {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      .button-group {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      button {
        border: none;
        width: 100%;
      }

      :host([part='wholesale']) fg-usps {
        display: none;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          display: none;
        }
      }
    `,
  ];
}
